import { ThemedIcon } from "components/icons";
import { useThemeApp } from "components/Theme/theme";
import { useState, forwardRef, useEffect } from "react";
import { masks, MasksType } from "common/src/other/masks";
import { ThemedInputContainer, ThemedInputContainerProps } from "../ThemedInputContainer";
import { Box, TextField, IconButton, TextFieldProps, InputAdornment, useMediaQuery, useTheme } from "@mui/material";

export type InputProps =
    Omit<TextFieldProps, "label" | "placeholder" | "onChange" | "error"> & Omit<ThemedInputContainerProps, "children"> & {
        step?: number;
        mask?: MasksType;
        minValue?: number;
        maxValue?: number;
        disabled?: boolean;
        placeholder: string;
        height?: string | number;
        secureTextEntry?: boolean;
        value?: string | undefined;
        showNumberControls?: boolean;
        onSubmitEditing?: () => void;
        onChangeText?: (text: string) => void;
        keyboardType?: "default" | "email-address" | "numeric" | "number";
    };



export const ThemedInput = forwardRef<HTMLDivElement, InputProps>(
    (
        {
            icon,
            mask,
            rows,
            label,
            value,
            height,
            inputId,
            maxValue,
            step = 1,
            minValue,
            multiline,
            inputMode,
            placeholder,
            onChangeText,
            heightContent,
            onSubmitEditing,
            disabled = false,
            secureTextEntry = false,
            keyboardType = "default",
            showNumberControls = false,
            ...rest
        },
        ref
    ) => {
        const theme = useTheme();
        const { colors } = useThemeApp();
        const [showPassword, setShowPassword] = useState(false);
        const [inputValue, setInputValue] = useState(value || "");
        const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

        const iconSize = isSmallScreen ? 20 : 24


        useEffect(() => {
            setInputValue(value || "");
        }, [value]);

        const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            let newValue = event.target.value;

            if (mask && masks[mask]) {
                newValue = masks[mask](String(newValue));
            }

            if (keyboardType === "numeric" || keyboardType === "number") {
                newValue = newValue.replace(/[^0-9]/g, '');
            }

            setInputValue(newValue);
            onChangeText && onChangeText(newValue);
        };

        const handleNumberChange = (increment: number) => {
            const currentValue = parseInt(inputValue) || 0;
            let newValue = currentValue + increment;

            if (minValue !== undefined && newValue < minValue) {
                newValue = minValue;
            }
            if (maxValue !== undefined && newValue > maxValue) {
                newValue = maxValue;
            }

            const newValueStr = newValue.toString();
            setInputValue(newValueStr);
            onChangeText && onChangeText(newValueStr);
        };

        const handleKeyDown = (event: React.KeyboardEvent) => {
            if (event.key === "Enter" && onSubmitEditing && !multiline) {
                onSubmitEditing();
                event.preventDefault();
            }

            if (showNumberControls && (keyboardType === "numeric" || keyboardType === "number")) {
                if (event.key === "ArrowUp") {
                    event.preventDefault();
                    handleNumberChange(step);
                } else if (event.key === "ArrowDown") {
                    event.preventDefault();
                    handleNumberChange(-step);
                }
            }
        };

        const getInputType = () => {
            if (secureTextEntry && !showPassword) return "password";

            switch (keyboardType) {
                case "email-address":
                    return "email";
                case "numeric":
                case "number":
                    return "text";
                default:
                    return "text";
            }
        };

        const getInputProps = () => {
            const props: any = {
                inputMode: inputMode || (keyboardType === "numeric" || keyboardType === "number" ? "numeric" : undefined),
                onKeyDown: handleKeyDown,
            };


            const endAdornments = [];

            if (showNumberControls && (keyboardType === "numeric" || keyboardType === "number") && !disabled) {
                endAdornments.push(
                    <Box
                        key="number-controls"
                        sx={{
                            ml: 1,
                            gap: 0.9,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={() => handleNumberChange(step)}
                            disabled={disabled || (maxValue !== undefined && (parseInt(inputValue) || 0) >= maxValue)}
                            sx={{
                                p: 0.9,
                                height: '20px',
                                minWidth: '24px',
                                color: colors.primary,
                                borderRadius: '4px 4px 0 0',
                                bgcolor: colors.primary + '20',
                                '&:hover': {
                                    bgcolor: colors.primary + '40',
                                },
                                '&:disabled': {
                                    opacity: 0.3,
                                }
                            }}
                        >
                            <ThemedIcon
                                size={16}
                                color="primary"
                                type="material"
                                name="ExpandLess"
                            />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => handleNumberChange(-step)}
                            disabled={disabled || (minValue !== undefined && (parseInt(inputValue) || 0) <= minValue)}
                            sx={{
                                p: 0.9,
                                height: '20px',
                                minWidth: '24px',
                                color: colors.primary,
                                borderRadius: '0 0 4px 4px',
                                bgcolor: colors.primary + '20',
                                '&:hover': {
                                    bgcolor: colors.primary + '40',
                                },
                                '&:disabled': {
                                    opacity: 0.3,
                                }
                            }}
                        >
                            <ThemedIcon
                                size={16}
                                type="material"
                                color="primary"
                                name="ExpandMore"
                            />
                        </IconButton>
                    </Box>
                );
            }

            if (secureTextEntry) {
                endAdornments.push(
                    <IconButton
                        edge="end"
                        size="small"
                        key="password-toggle"
                        onClick={() => setShowPassword(!showPassword)}
                    >
                        <ThemedIcon
                            size={iconSize}
                            color={"border"}
                            animation="fade"
                            type={"material"}
                            name={showPassword ? "VisibilityOff" : "Visibility"}
                        />
                    </IconButton>
                );
            }

            if (endAdornments.length > 0) {
                props.endAdornment = (
                    <InputAdornment position="end" sx={{ padding: "0px !important" }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {endAdornments}
                        </Box>
                    </InputAdornment>
                );
            }

            return props;
        };


        return (
            <ThemedInputContainer
                icon={icon}
                inputId={inputId}
                multiline={multiline}
                heightContent={heightContent}
                justifyContentCenter={!multiline}
                label={label ? label : undefined}
            >
                <TextField
                    ref={ref}
                    fullWidth
                    value={inputValue}
                    variant="outlined"
                    disabled={disabled}
                    multiline={multiline}
                    type={getInputType()}
                    InputProps={getInputProps()}
                    onChange={handleOnChange as any}
                    rows={multiline ? (rows || 4) : undefined}
                    minRows={multiline ? (rows || 4) : undefined}
                    placeholder={placeholder ? placeholder : undefined}
                    inputProps={{
                        inputMode: inputMode || (keyboardType === "numeric" || keyboardType === "number" ? "numeric" : undefined),
                        style: {
                            paddingTop: multiline ? 12 : undefined,
                            paddingBottom: multiline ? 12 : undefined,
                            textAlign: showNumberControls ? 'center' : 'left',
                        },
                    }}
                    sx={{
                        height: "100%",
                        padding: "0px !important",
                        "& .MuiInputBase-input": {
                            padding: "0px !important",
                        },
                        "& input[type=number]": {
                            MozAppearance: 'textfield',
                        },
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                            display: 'none',
                            WebkitAppearance: 'none',
                        },
                        "& .MuiOutlinedInput-root": {
                            padding: "0px !important",
                        }
                    }}
                    {...rest}
                />
            </ThemedInputContainer>
        );
    }
);

ThemedInput.displayName = "ThemedInput";